/*eslint-disable*/
import React, { useState, useCallback, useMemo, useRef } from "react";
import { Box, Input, Paper, Popper, List, ListItem, Tooltip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

// Define searchable fields specific to enquiries
const searchableEnquiryFields = [
  "userdetials.firstName",
  "userdetials.lastName",
  "userdetials.email",
  "OwnerName",
  "userdetials.phoneNumber",
  "clientDetails.firstName",
  "clientDetails.lastName",
  "clientDetails.email",
  "clientDetails.phoneNumber",
  "businessName",
  "typeOfProperty",
  "type",
  "web_Ref_No",
  "propertyId",
];

const enquiryFieldTooltip = `
Search Across: Broker Name, Broker Email, Broker Phone, Client Name, Client Email, Client Phone,
Property Type, Sale Type, Web Reference Number, Property ID, Company Name, Owner Name
`;

// Memoized nested value getter
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, key) => acc?.[key] ?? "", obj);
};

const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

const SearchWithSuggestionsEnquiries = ({ currentEnquiries = [], setFilteredEnquiries }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  // Memoize currentEnquiries to prevent unnecessary recalculations
  const memoizedEnquiries = useMemo(() => currentEnquiries, [currentEnquiries]);

  // Optimized search function
  const performEnquirySearch = useCallback((valueToSearch) => {
    const value = valueToSearch.toLowerCase().trim();

    if (!value) {
      setSuggestions([]);
      setFilteredEnquiries(memoizedEnquiries);
      return;
    }

    // Use Set for faster lookups and avoid duplicates
    const seenValues = new Set();
    const matchedEnquiries = [];
    const uniqueMatches = [];

    // Single pass through enquiries
    memoizedEnquiries.forEach((enquiry) => {
      let isMatched = false;

      searchableEnquiryFields.forEach((field) => {
        const fieldValue = getNestedValue(enquiry, field);
        if (fieldValue && String(fieldValue).toLowerCase().includes(value)) {
          isMatched = true;
          const suggestionText = String(fieldValue).toLowerCase();

          if (!seenValues.has(suggestionText)) {
            seenValues.add(suggestionText);
            uniqueMatches.push({ Name: suggestionText });
          }
        }
      });

      if (isMatched) {
        matchedEnquiries.push(enquiry);
      }
    });

    setFilteredEnquiries(matchedEnquiries);
    setSuggestions(uniqueMatches.slice(0, 10));
  }, [memoizedEnquiries, setFilteredEnquiries]);

  // Debounced search with 150ms delay
  const debouncedSearch = useDebounce(performEnquirySearch, 150);

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setInput(value);

    if (value.trim().length === 0) {
      // Immediate response for empty search
      setSuggestions([]);
      setFilteredEnquiries(memoizedEnquiries);
    } else if (value.trim().length >= 2) {
      // Only search if at least 2 characters
      debouncedSearch(value);
    } else {
      // Clear suggestions for 1 character
      setSuggestions([]);
    }
  }, [debouncedSearch, memoizedEnquiries, setFilteredEnquiries]);

  const handleSuggestionClick = useCallback((suggestionItem) => {
    setInput(suggestionItem.Name);
    performEnquirySearch(suggestionItem.Name);
    setAnchorEl(null);
  }, [performEnquirySearch]);

  const handleClear = useCallback(() => {
    setInput("");
    setSuggestions([]);
    setFilteredEnquiries(memoizedEnquiries);
  }, [memoizedEnquiries, setFilteredEnquiries]);

  const handleSearchClick = useCallback(() => {
    performEnquirySearch(input);
  }, [input, performEnquirySearch]);

  return (
    <Box sx={{ position: "relative", width: 500 }}>
      <Tooltip title={enquiryFieldTooltip} arrow placement="top">
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", position: "relative" }}>
          <Input
            fullWidth
            placeholder="Search by Keyword"
            value={input}
            onChange={handleSearch}
            onFocus={(e) => setAnchorEl(e.currentTarget)}
            onBlur={() => setTimeout(() => setAnchorEl(null), 200)}
            sx={{
              borderWidth: 2,
              borderStyle: "solid",
              borderColor: "#002147",
              height: 54,
              backgroundColor: "white",
              fontSize: 16,
              borderRadius: 0,
              color: "grey",
              paddingRight: 3,
              px: 2,
            }}
          />
          <IconButton
            size="small"
            onClick={handleClear}
            disabled={!input}
            sx={{ position: "absolute", right: 48, zIndex: 2 }}
          >
            <CloseIcon fontSize="small" sx={{ color: (theme) => theme.palette.primary.main }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleSearchClick}
            sx={{ position: "absolute", right: 8 }}
          >
            <SearchIcon sx={{ color: (theme) => theme.palette.primary.main }} />
          </IconButton>
        </Box>
      </Tooltip>

      <Popper
        open={!!anchorEl && suggestions.length > 0}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1500, width: anchorEl ? anchorEl.clientWidth : "auto" }}
      >
        <Paper sx={{ mt: 1, p: 1, maxHeight: 300, overflow: 'auto' }}>
          <List dense>
            {suggestions.map((item, index) => (
              <ListItem
                key={index}
                onClick={() => handleSuggestionClick(item)}
                sx={{ cursor: "pointer", fontSize: "0.9rem", "&:hover": { backgroundColor: "#f0f0f0" } }}
              >
                {item.Name}
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};

export default React.memo(SearchWithSuggestionsEnquiries);
